import React from "react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MailCheck, MailOpen, MailX, MailWarning, Send, Mail } from "lucide-react";

// Per-status presentation: label, chip classes, and icon.
const STATUS_META = {
  open: { label: "Opened", cls: "bg-[#f3e7cb] text-[#7a5f22] border-[#e3cf9e]", Icon: MailOpen },
  delivered: { label: "Delivered", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: MailCheck },
  bounce: { label: "Bounced", cls: "bg-red-100 text-red-800 border-red-200", Icon: MailX },
  dropped: { label: "Dropped", cls: "bg-red-100 text-red-800 border-red-200", Icon: MailX },
  spamreport: { label: "Spam", cls: "bg-orange-100 text-orange-800 border-orange-200", Icon: MailWarning },
  deferred: { label: "Deferred", cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: MailWarning },
  processed: { label: "Sent", cls: "bg-blue-100 text-blue-800 border-blue-200", Icon: Send },
  sent: { label: "Sent", cls: "bg-blue-100 text-blue-800 border-blue-200", Icon: Send },
  failed: { label: "Failed", cls: "bg-red-100 text-red-800 border-red-200", Icon: MailWarning },
};

const meta = (s) => STATUS_META[s] || STATUS_META.sent;

function ts(v) {
  if (!v) return "";
  try { return new Date(v).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}

// Pick a single headline status across recipients. Problems win so they're never
// hidden; otherwise the most positive engagement is shown.
function headline(recipients) {
  const statuses = recipients.map((r) => r.status);
  const has = (s) => statuses.includes(s);
  if (has("bounce")) return "bounce";
  if (has("dropped")) return "dropped";
  if (has("spamreport")) return "spamreport";
  if (has("open")) return "open";
  if (has("delivered")) return "delivered";
  if (has("deferred")) return "deferred";
  if (has("failed")) return "failed";
  return "sent";
}

/**
 * Compact chip showing the delivery status of the reminder email sent for an
 * article. Click to see per-recipient delivered/opened/bounced detail.
 */
export default function DeliveryStatusChip({ delivery, articleId }) {
  if (!delivery || !Array.isArray(delivery.recipients) || delivery.recipients.length === 0) return null;
  const recipients = delivery.recipients;
  const head = headline(recipients);
  const { label, cls, Icon } = meta(head);
  const n = recipients.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a86a] cursor-pointer"
          data-testid={`reminder-delivery-chip-${articleId}`}
          aria-label={`Reminder email ${label}`}
        >
          <Badge variant="outline" className={`${cls} gap-1 font-medium`}>
            <Icon size={11} /> {label}{n > 1 ? ` · ${n}` : ""}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80" data-testid={`reminder-delivery-popover-${articleId}`}>
        <div className="flex items-center gap-2 mb-2">
          <Mail size={15} className="text-[#c9a86a]" />
          <div className="text-sm font-medium leading-tight">Reminder email</div>
        </div>
        <p className="text-[11px] text-[var(--muted)] mb-3">
          Sent {ts(delivery.created_at)} · {delivery.subject}
        </p>
        {delivery.send_error && (
          <p className="text-[11px] text-red-700 mb-2">Send error: {delivery.send_error}</p>
        )}
        <ul className="space-y-2">
          {recipients.map((r) => {
            const m = meta(r.status);
            const RIcon = m.Icon;
            const when = r.opened_at || r.delivered_at || r.bounced_at || r.dropped_at || r.last_event_at || r.sent_at;
            return (
              <li key={r.email} className="flex items-start justify-between gap-2" data-testid={`reminder-delivery-recipient-${r.email}`}>
                <div className="min-w-0">
                  <div className="text-xs truncate">{r.email}</div>
                  {r.reason && <div className="text-[11px] text-red-700 truncate" title={r.reason}>{r.reason}</div>}
                  {when && <div className="text-[10px] text-[var(--muted)]">{ts(when)}</div>}
                </div>
                <Badge variant="outline" className={`${m.cls} gap-1 shrink-0`}>
                  <RIcon size={11} /> {m.label}
                </Badge>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
